import {
  type DesktopCompanionMessage,
  Plugins,
  type DesktopCompanionStatusChangedEvent,
  type DesktopCompanionMessageEvent,
  DesktopCompanionConnectionStatus,
  Container,
} from '@getgo/container-client'
import {
  type ExternalInterfaceConnectionCallback,
  type ExternalInterface,
  type ExternalInterfaceCallback,
  type ExternalInterfaceMessage,
} from './external-interface'
import { getWindow } from '../../common/dom-helpers'
import { externalInterfaceConnectionHandler } from './external-interface-connection-handler'
import { externalInterfaceCommandDispatcher } from './external-interface-command-dispatcher'
import { getContainerInfo } from '../container/container-info'
import { MS_TEAMS_INTEGRATION_ID, getIntegrationMetaData } from '../../core/integrations-helpers'
import { getExternalInterfacePorts } from '../../core/environment'

//These are some randomly generated unreserved ports, we want to ensure that they are not in use so as not to break the integration for some customers.
export class ExternalInterfaceAdapter implements ExternalInterface {
  private readonly handlers: Map<(event: DesktopCompanionMessageEvent) => void, ExternalInterfaceCallback<any>> =
    new Map()
  private readonly callerId = `external-interface-${Math.random()}`
  private pendingMessages: ExternalInterfaceMessage[] = []

  get available() {
    return Plugins.DesktopCompanion.connectionStatus === DesktopCompanionConnectionStatus.connected
  }

  get isCompanion() {
    return getContainerInfo()?.flavor === MS_TEAMS_INTEGRATION_ID
  }

  get isIntegration() {
    return getIntegrationMetaData()?.id === MS_TEAMS_INTEGRATION_ID && !Container.isNative
  }

  constructor() {
    this.addConnectionCallback(this.handleConnectionStatusChanged)
  }

  connect(): void {
    Plugins.DesktopCompanion.startConnecting({ ports: getExternalInterfacePorts() })
  }

  disconnect(): void {
    Plugins.DesktopCompanion.disconnect()
  }

  send<M extends ExternalInterfaceMessage>(msg: M): void {
    if (this.available) {
      Plugins.DesktopCompanion.send({
        ...msg,
        payload: { ...msg.payload, callerId: this.callerId },
      } as DesktopCompanionMessage)
    } else {
      this.pendingMessages.push(msg)
    }
  }

  addCallback<EIM extends keyof ExternalInterfaceMessageMap>(
    type: EIM,
    callback: ExternalInterfaceCallback<ExternalInterfaceMessageMap[EIM]['payload']>,
  ): void
  addCallback<EIM extends ExternalInterfaceMessage>(
    type: EIM['type'],
    callback: ExternalInterfaceCallback<EIM['payload']>,
  ): void {
    const handler = (event: DesktopCompanionMessageEvent) => {
      if ('callerId' in event.message.payload && event.message.payload.callerId !== this.callerId) {
        callback(event.message.payload)
      }
    }
    this.handlers.set(callback, handler)
    Plugins.DesktopCompanion.addMessageEventListener(type as never, handler)
  }

  removeCallback<EIM extends keyof ExternalInterfaceMessageMap>(
    type: EIM,
    callback: ExternalInterfaceCallback<ExternalInterfaceMessageMap[EIM]['payload']>,
  ): void
  removeCallback<EIM extends ExternalInterfaceMessage>(
    type: EIM['type'],
    callback: ExternalInterfaceCallback<EIM['payload']>,
  ): void {
    const handler = this.handlers.get(callback)
    if (handler) {
      Plugins.DesktopCompanion.removeMessageEventListener(type as never, handler)
    }
  }

  addConnectionCallback(callback: ExternalInterfaceConnectionCallback): void {
    Plugins.DesktopCompanion.addEventListener('connectionStatusChanged', callback)
  }

  removeConnectionCallback(callback: ExternalInterfaceConnectionCallback): void {
    Plugins.DesktopCompanion.removeEventListener('connectionStatusChanged', callback)
  }

  private readonly handleConnectionStatusChanged = (e: DesktopCompanionStatusChangedEvent) => {
    if (e.connectionStatus === DesktopCompanionConnectionStatus.connected) {
      this.pendingMessages.forEach(msg => {
        this.send(msg)
      })

      this.pendingMessages = []
    }
  }
}

const createExternalInterface = () => {
  const externalInterface = new ExternalInterfaceAdapter()
  externalInterfaceConnectionHandler(externalInterface) // move this after the Container.getInfo().flavor
  externalInterfaceCommandDispatcher(externalInterface)
  return externalInterface
}

const getOpenerExternalInterface = () => {
  // In some cases, we might have a Cross Origin Window opening up a Shell based app
  // from another location
  try {
    return getWindow().opener?.shellExternalInterface
  } catch {
    return undefined
  }
}

export const getExternalInterface = () => {
  if (!getWindow().shellExternalInterface) {
    getWindow().shellExternalInterface = getOpenerExternalInterface() ?? createExternalInterface()
  }

  return getWindow().shellExternalInterface as ExternalInterface
}
